import {
    Component,
    ElementRef,
    input,
    Input,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import { MatInputModule } from '@angular/material/input';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Observable, of } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { IconComponent } from '@nagel-assests';
import { FieldBase, FieldOption } from '@nagel-types';
import { BaseFieldComponent } from '../../base-field/base-field.component';
import { AutocompleteService } from '../autocompleteService/autocomplete.service';
import { HighlightTextDirective, LazyLoadDirective } from '@nagel-directives';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AutocompleteRequestFilterCriterias } from 'libs/nagel-types/src/lib/fieldDefinitionOptions';
import { FILTER_OPERATORS } from '../../../formConst';
import { ErrorStateMatcher } from '@angular/material/core';
import { getCommonErrorMessage } from '@nagel-utils';

@Component({
    selector: 'lib-autocomplete',
    imports: [
        FormsModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        ReactiveFormsModule,
        MatInputModule,
        MatInputModule,
        IconComponent,
        AsyncPipe,
        HighlightTextDirective,
        MatProgressSpinnerModule,
        LazyLoadDirective,

    ],
    providers: [AutocompleteService],
    templateUrl: './autocomplete.component.html',
    styleUrl: './autocomplete.component.scss'
})
export class AutocompleteComponent extends BaseFieldComponent implements OnInit {
    @Input() override field!: FieldBase<unknown>;
    @Input() override form!: FormGroup;
    errorStateMatcher = input<ErrorStateMatcher>(new ErrorStateMatcher());
    showWarningState = input<boolean>(false);
    hint = input<string>('');
    icon = input< string | undefined >(undefined);

    private input = viewChild.required<ElementRef<HTMLInputElement>>('input');

    filteredOptions!: Observable<FieldOption[]>;
    options: FieldOption[] = [];
    shouldIgnoreOnChange = signal<boolean>(false);

    constructor(public autocompleteService: AutocompleteService) {
        super();
    }

    ngOnInit() {
        if (this.field.value) {
            this.form.get(this.field.key)?.patchValue(this.field.value, { emitEvent: false });
        }

        if (this.field.options.length > 0) {
            this.filteredOptions = of(this.field.options);
            this.options = this.field.options;
        }

        this.autocompleteService.optionsObservable$
            .subscribe((options: FieldOption[]) => {
                const newOptions = (options || [])
                    .map((option: FieldOption) => {
                        return {
                            viewValue: option.viewValue,
                            value: option.value,
                            rawValue: option.rawValue
                        };
                    });

                if (this.autocompleteService.startIndex === 0 && this.field.url) {
                    this.options = [];
                }
                this.options = [...this.options, ...newOptions];

                this.filteredOptions = of(this._filter(this.options, ''));
            });

        this.handleOnAutocompleteOpen();
    }

    private makeFilterRequest = (filterCriterias: AutocompleteRequestFilterCriterias[]) => {
        this.autocompleteService.shouldFireRequest$.next({
            url: this.field.url || '',
            filterCriterias,
            responseMapperFn: this.field.responseMapperFn ?? ((response: any) => [])
        });
    };

    handleOnAutocompleteOpen = () => {
        this.field.onOpen && this.field.onOpen(this.onOpenSuccessHandler);
    };

    onOptionSelected = (event: MatAutocompleteSelectedEvent): void => {
        const selectedOption = event.option.value;

        this.filteredOptions = of(this.options.filter(option => option.value === selectedOption.value));

        this.field.onSelect && this.field.onSelect(selectedOption, this.field.key);

        this.shouldIgnoreOnChange.set(true);
    };

    onBlur = (event: FocusEvent) => {
        const searchTerm = this.input()?.nativeElement.value;
        const potentialMatches = this._filter(this.options, searchTerm || '');

        //If there is form use this else use the passed onBlur from the config

        if (potentialMatches.length && searchTerm === potentialMatches[0].viewValue) {
            const valueToApply = potentialMatches[0];
            this.form.controls[this.field.key].setValue(valueToApply);
        }

        if (this.field.onBlur) {
            this.field.onBlur(event);
        }
    };

    onFocus = () => {
        if (!this.field.readonly) {
            this.onInputChange();
        }
    };

    onInputChange = () => {
        const searchTerm = this.input()?.nativeElement.value;
        this.filteredOptions = of(this._filter(this.options, searchTerm || ''));
        const formControl = this.form.controls[this.field.key];
        const formControlValue = formControl.value;

        if (this.field.url && this.field.onChange && !this.shouldIgnoreOnChange()) {
            this.autocompleteService.startIndex = 0;
            this.autocompleteService.options$.next([]);

            const filterCriterias = this.field.requestMapperFn
                ? this.field.requestMapperFn(searchTerm)
                : [
                    {
                        fieldName: this.field.fieldName || this.field.key,
                        value: searchTerm,
                        filterOperator: FILTER_OPERATORS.CONTAINS_IGNORE_CASE,
                        isCalc: false
                    }
                ];

            this.fireFilterRequest(filterCriterias);

            const isNotChanged = formControlValue && formControlValue.viewValue === searchTerm;

            if (!isNotChanged) {
                this.field.onChange({ value: searchTerm, key: this.field.key, options: this.options });
            }
        }

        if (this.shouldIgnoreOnChange()) {
            this.shouldIgnoreOnChange.set(false);
        }
    };

    fireFilterRequest = (filterCriterias: AutocompleteRequestFilterCriterias[]) => {
        if (this.field.url) {
            this.makeFilterRequest(filterCriterias);
        }
    };

    private _filter(options: FieldOption[], searchTerm: string): FieldOption[] {
        const filterValue = searchTerm.toLowerCase();

        return options.filter(option => option.viewValue.toLowerCase().includes(filterValue));
    }

    onOpenSuccessHandler = (data: FieldOption[], searchTerm: string = '') => {
        this.options = data;
        this.filteredOptions = of(this._filter(this.options, searchTerm || ''));
    };

    getOptionIcons = (iconString: string | string[] | undefined): string[] => {
        let result: string[] = [];

        if (iconString) {
            result = Array.isArray(iconString)
                ? iconString
                : [iconString];
        }

        return result;
    };

    displayWith = (option: FieldOption): string => {
        return option?.viewValue || '';
    };

    getHighlightTerm = (): string => {
        return this.input()?.nativeElement.value || '';
    };

    loadMoreItems = () => {
        if (this.options.length && this.field.shouldHaveLazyLoad) {
            this.autocompleteService.startIndex++;
            const searchTerm = this.input()?.nativeElement.value;
            const filterCriterias = this.field.requestMapperFn
                ? this.field.requestMapperFn(searchTerm)
                : [
                    {
                        fieldName: this.field.fieldName || this.field.key,
                        value: searchTerm,
                        filterOperator: FILTER_OPERATORS.CONTAINS_IGNORE_CASE,
                        isCalc: false
                    }
                ];

            this.fireFilterRequest(filterCriterias);
            this.autocompleteService.isPaginating$.next(true);
        }
    };

    getSuffixIcon = () => {
        return this.field.icon || this.icon() || '';
    };

    focus = () => {
        this.input()?.nativeElement.focus();
    };

    getCommonErrors = () => {
        const control = this.form.get(this.field.key);
        let result = getCommonErrorMessage(control?.errors || null, this.field.label);

        if (control?.errors && !result) {
            const { message } = Object.values(control?.errors || {})[0] || {};

            if (message) {
                result = message;
            }
        }

        return result;
    };
}